import styles from "./AppDynamicDropdown.module.scss";
import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import Form from "react-bootstrap/Form";
import clsx from "clsx";
import {useDebounce} from "use-debounce";

export const AppDynamicDropdown = ({
                                     value,
                                     choicesQuery,
                                     onChange,
                                     isInvalid,
                                     choiceValue = "value",
                                     choiceLabel = "label",
                                     choiceDisplay = DefaultChoiceDisplay,
                                     id = null,
                                     disabled = false
                                   }) => {
  const [choicesDisplayed, setChoicesDisplayed] = useState(false);
  const [activeChoice, setActiveChoice] = useState(null);
  const [choices, setChoices] = useState([]);

  const valueChoice = value ? choices.find(c => c[choiceValue] === value[choiceValue]) || null : null;

  const [inputValue, setInputValue] = useState(value ? value[choiceLabel] : "");

  const [debouncedFilter] = useDebounce(inputValue, 200);

  const dropdownRef = useRef();
  const choicesRef = useRef();

  useEffect(() => {
    const onMousedown = e => {
      if (!dropdownRef.current.contains(e.target)) {
        setChoicesDisplayed(p => false);
      }
    };

    document.addEventListener("mousedown", onMousedown);

    return () => {
      document.removeEventListener("mousedown", onMousedown);
    };
  }, []);

  const loadChoices = useCallback(q => {
    choicesQuery(q).then(data => setChoices(data.content));
  }, [choicesQuery]);

  useEffect(() => {
    loadChoices(debouncedFilter);
  }, [loadChoices, debouncedFilter]);

  const showChoices = () => {
    setChoicesDisplayed(prev => !prev);
    setActiveChoice(valueChoice);

    if (valueChoice) {
      syncScroll(choices.findIndex(c => c[choiceValue] === valueChoice[choiceValue]));
    }
  };

  const syncScroll = index => {
    if (!choicesRef.current) {
      return;
    }

    const element = choicesRef.current.getElementsByClassName("choice")[index];

    if (element.offsetTop + element.offsetHeight > choicesRef.current.scrollTop + choicesRef.current.offsetHeight) {
      choicesRef.current.scrollTo(0, element.offsetTop + element.offsetHeight - choicesRef.current.offsetHeight + 10);
    } else if (element.offsetTop < choicesRef.current.scrollTop) {
      choicesRef.current.scrollTo(0, index === 0 ? 0 : element.offsetTop - 10);
    }
  }

  const onKeyDown = e => {
    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (!choicesDisplayed) {
        showChoices();

        return;
      }

      let newIndex;

      if (activeChoice === null || activeChoice === undefined) {
        newIndex = 0;
      } else {
        const index = choices.findIndex(c => c[choiceValue] === activeChoice[choiceValue]);

        newIndex = index + 1 === choices.length ? 0 : index + 1;
      }

      activateChoice(choices[newIndex]);

      syncScroll(newIndex);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();

      if (!choicesDisplayed) {
        showChoices();

        return;
      }

      let newIndex;

      if (activeChoice === null) {
        newIndex = choices.length - 1;
      } else {
        const index = choices.findIndex(c => c[choiceValue] === activeChoice[choiceValue]);

        newIndex = index - 1 < 0 ? choices.length - 1 : index - 1;
      }

      activateChoice(choices[newIndex]);

      syncScroll(newIndex);
    } else if (choicesDisplayed && e.key === "Enter") {
      if (activeChoice) {
        e.preventDefault();

        chooseChoice(activeChoice);
      }
    } else if (e.key === "Escape") {
      if (choicesDisplayed) {
        setChoicesDisplayed(false);
      }
    } else if (e.key === "Tab") {
      if (choiceDisplay) {
        setChoicesDisplayed(false);
      }
    }
  };

  const activateChoice = choice => {
    if (choice !== activeChoice) {
      setActiveChoice(choice);
    }
  };

  const chooseChoice = choice => {
    const newValue = choice ? choice[choiceValue] : value;

    if (newValue !== value) {
      setInputValue(choice[choiceLabel]);

      onChange(choice);
    }

    setActiveChoice(choice);
    setChoicesDisplayed(false);
  };

  return <div
    className={clsx(styles.dropdown, choicesDisplayed ? styles.visible : null, disabled ? styles.disabled : null)}
    ref={dropdownRef}
  >
    <Form.Control
      type="text"
      className={styles.inputPlaceholder}
      isInvalid={isInvalid}
      value={inputValue}
      onFocus={e => loadChoices(e.target.value)}
      onChange={e => {
        setInputValue(e.target.value);

        if (!choicesDisplayed) {
          showChoices();
        }
      }}
      onKeyDown={onKeyDown}
      onMouseDown={showChoices}
      id={id}
      disabled={disabled}
    />

    <span className={clsx("material-icons", styles.dropdownIcon)}>arrow_drop_down</span>

    <div className={styles.choices} ref={choicesRef}>
      {choices.map(choice => {
        return <Fragment key={choice[choiceValue]}>
          <div
            className={clsx(styles.choice, activeChoice === choice ? styles.active : null, "choice")}
            onClick={() => chooseChoice(choice)}
            onMouseEnter={() => activateChoice(choice)}
          >
            {choiceDisplay(choice)}
          </div>
        </Fragment>;
      })}
    </div>
  </div>;
};

const DefaultChoiceDisplay = choice => <div>{choice.label}</div>;
